import type { JSX } from 'react';

import type { ExecutionEstimateV1 } from '@/lib/api/quality-types.js';
import { cn } from '@/lib/utils.js';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip.js';

const CANONICAL_DECIMAL = /^(?:0|0\.\d*[1-9]|[1-9]\d*(?:\.\d*[1-9])?)$/;
const ESTIMATE_TIERS = new Set(['standard', 'priority', 'flex', 'unknown']);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isCount(value: unknown): value is number {
  return Number.isInteger(value) && Number(value) >= 0;
}

function isExecutionEstimateV1(value: unknown): value is ExecutionEstimateV1 {
  if (!isRecord(value)) return false;

  const methodCounts = value.method_counts;
  const requestCoverage = value.request_coverage;
  const reasonCounts = value.reason_counts;
  const bases = value.bases;
  const countFields = [
    'known_request_count',
    'priced_request_count',
    'unknown_request_count',
    'observed_invocation_count',
    'required_parent_count',
    'sealed_parent_count',
    'stale_rate_request_count',
  ] as const;

  return (
    value.schema_version === 1 &&
    value.currency === 'USD' &&
    value.scope === 'quality_executor_requests' &&
    (value.amount_usd === null || typeof value.amount_usd === 'string') &&
    typeof value.amount_kind === 'string' &&
    isRecord(methodCounts) &&
    isCount(methodCounts.catalog_calculation) &&
    methodCounts.provider_reported === 0 &&
    countFields.every((field) => isCount(value[field])) &&
    (value.total_request_count === null ||
      isCount(value.total_request_count)) &&
    (requestCoverage === null ||
      (isRecord(requestCoverage) &&
        isCount(requestCoverage.priced) &&
        isCount(requestCoverage.total))) &&
    (value.expected_invocation_count === null ||
      isCount(value.expected_invocation_count)) &&
    typeof value.request_population_complete === 'boolean' &&
    typeof value.invocation_population_complete === 'boolean' &&
    isRecord(reasonCounts) &&
    Object.values(reasonCounts).every(isCount) &&
    Array.isArray(bases) &&
    bases.every(
      (basis) =>
        isRecord(basis) &&
        basis.provider === 'deepinfra' &&
        typeof basis.model === 'string' &&
        typeof basis.tier === 'string' &&
        ESTIMATE_TIERS.has(basis.tier) &&
        basis.method === 'catalog_calculation' &&
        Array.isArray(basis.snapshot_ids) &&
        basis.snapshot_ids.every((snapshot) => typeof snapshot === 'string') &&
        typeof basis.amount_usd === 'string' &&
        isCount(basis.priced_request_count) &&
        typeof basis.fetched_at_from === 'string' &&
        typeof basis.fetched_at_through === 'string',
    ) &&
    typeof value.bases_truncated === 'boolean' &&
    (value.as_of === null || typeof value.as_of === 'string') &&
    typeof value.revision === 'string' &&
    typeof value.final === 'boolean'
  );
}

/** Rounds canonical accounting amounts for display; raw service data stays exact. */
export function formatExecutionEstimateUsd(amount: string | null): string {
  if (amount === null || amount.length > 128 || !CANONICAL_DECIMAL.test(amount))
    return 'Unknown';
  if (amount === '0') return '$0.00';
  const [whole, fraction = ''] = amount.split('.');
  if (BigInt(whole!) === 0n && !/[1-9]/.test(fraction.slice(0, 2)))
    return '<$0.01';
  const cents =
    BigInt(whole!) * 100n +
    BigInt(fraction.slice(0, 2).padEnd(2, '0')) +
    (Number(fraction[2] ?? '0') >= 5 ? 1n : 0n);
  return `$${(cents / 100n).toLocaleString('en-US')}.${String(cents % 100n).padStart(2, '0')}`;
}

export function formatRecordedUsd(
  amount: number | string | null,
): string | null {
  if (amount === null || (typeof amount === 'string' && amount.trim() === ''))
    return null;
  const value = Number(amount);
  if (!Number.isFinite(value) || value < 0) return null;
  if (value > 0 && value < 0.01) return '<$0.01';
  return `$${value.toFixed(2)}`;
}

export function executionEstimateText(estimate: unknown): string {
  if (!isExecutionEstimateV1(estimate)) return 'Unknown';
  const amount = formatExecutionEstimateUsd(estimate.amount_usd);
  switch (estimate.amount_kind) {
    case 'complete':
      return amount;
    case 'subtotal':
      return amount === 'Unknown' ? 'Unknown' : `${amount} · Partial estimate`;
    case 'unknown':
      return 'Unknown';
    case 'proven_zero':
      return estimate.amount_usd === '0' ? '$0.00' : 'Unknown';
    default:
      return 'Unknown';
  }
}

export function executionEstimatePlainText(
  estimate: unknown,
  label = 'Estimated execution cost',
): string {
  if (!isExecutionEstimateV1(estimate)) return `${label}: Unknown`;
  return [
    `${label}: ${executionEstimateText(estimate)}`,
    ...provenanceParts(
      estimate,
      estimate.final
        ? 'Final estimate'
        : 'Estimate may change as evidence arrives',
    ),
  ].join('; ');
}

function requestCoverageText(estimate: ExecutionEstimateV1): string {
  if (estimate.request_coverage) {
    const population = estimate.request_population_complete
      ? ''
      : '; request population incomplete';
    return `${estimate.request_coverage.priced}/${estimate.request_coverage.total} requests priced${population}`;
  }
  if (estimate.known_request_count > 0) {
    return `${estimate.priced_request_count}/${estimate.known_request_count} known requests priced; total request population unknown`;
  }
  return estimate.amount_kind === 'proven_zero' &&
    estimate.request_population_complete
    ? 'No requests sent in the complete scoped population'
    : 'Total request population unknown';
}

function invocationCoverageText(estimate: ExecutionEstimateV1): string {
  if (estimate.expected_invocation_count === null) {
    return `${estimate.observed_invocation_count} invocation${estimate.observed_invocation_count === 1 ? '' : 's'} observed; total invocation population unknown`;
  }
  const population = estimate.invocation_population_complete
    ? ''
    : '; invocation population incomplete';
  return `${estimate.observed_invocation_count}/${estimate.expected_invocation_count} invocations observed${population}`;
}

function parentCoverageText(estimate: ExecutionEstimateV1): string {
  return `${estimate.sealed_parent_count}/${estimate.required_parent_count} required parents sealed`;
}

function scopeText(): string {
  return 'Scope: Quality executor requests only; other run costs are excluded';
}

function reasonsText(estimate: ExecutionEstimateV1): string {
  const reasons = Object.entries(estimate.reason_counts)
    .filter(
      (entry): entry is [string, number] =>
        typeof entry[1] === 'number' && Number.isFinite(entry[1]),
    )
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([reason, count]) => `${reason}: ${count}`);
  return `Estimate reasons: ${reasons.length > 0 ? reasons.join(', ') : 'none'}`;
}

function asOfText(estimate: ExecutionEstimateV1): string {
  return `Evidence as of: ${estimate.as_of ?? 'unavailable'}`;
}

function basisText(estimate: ExecutionEstimateV1, index: number): string {
  const basis = estimate.bases[index];
  if (!basis) return '';
  const snapshots =
    basis.snapshot_ids.length > 0
      ? `rate snapshots ${basis.snapshot_ids.join(', ')}`
      : 'no rate snapshot IDs reported';
  const fetched =
    basis.fetched_at_from === basis.fetched_at_through
      ? `catalog fetched ${basis.fetched_at_from}`
      : `catalog fetched ${basis.fetched_at_from} through ${basis.fetched_at_through}`;
  return `${basis.provider} · ${basis.model} · ${basis.tier} · ${formatExecutionEstimateUsd(basis.amount_usd)} from ${basis.priced_request_count} request${basis.priced_request_count === 1 ? '' : 's'} · ${snapshots} · ${fetched}`;
}

function provenanceParts(
  estimate: ExecutionEstimateV1,
  finality = estimate.final ? 'Final' : 'May change as evidence arrives',
): string[] {
  const bases = estimate.bases.map((_, index) => basisText(estimate, index));
  return [
    requestCoverageText(estimate),
    invocationCoverageText(estimate),
    parentCoverageText(estimate),
    scopeText(),
    finality,
    `Stale-rate requests: ${estimate.stale_rate_request_count}`,
    reasonsText(estimate),
    asOfText(estimate),
    `Revision: ${estimate.revision}`,
    ...(bases.length > 0 ? bases : ['No priced basis available']),
    ...(estimate.bases_truncated
      ? ['Additional pricing bases are omitted']
      : []),
  ];
}

function estimateExplanation(estimate: ExecutionEstimateV1): string {
  if (executionEstimateText(estimate) === 'Unknown')
    return 'Model usage is unavailable, so cost cannot be estimated.';
  if (estimate.amount_kind === 'proven_zero')
    return 'No model requests were sent.';
  if (estimate.amount_kind === 'subtotal')
    return 'Some model usage is unavailable. The full cost may be higher.';
  return estimate.final
    ? 'Estimated cost of model requests.'
    : 'This estimate may change as usage is collected.';
}

export function ExecutionEstimate({
  estimate,
  label = 'Execution estimate',
  details = false,
  className,
}: {
  estimate?: ExecutionEstimateV1 | null;
  label?: string;
  details?: boolean;
  className?: string;
}): JSX.Element | null {
  if (!isExecutionEstimateV1(estimate)) return null;

  const compact = (
    <span className="inline-flex min-w-0 max-w-full flex-wrap items-baseline gap-x-1">
      <span className="shrink-0">{label}:</span>
      <span className="min-w-0 break-all font-medium text-foreground">
        {executionEstimateText(estimate)}
      </span>
    </span>
  );

  if (!details) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            aria-label={`${label}: ${executionEstimateText(estimate)}. ${estimateExplanation(estimate)}`}
            className={cn(
              'min-w-0 max-w-full rounded-sm text-xs text-muted-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring',
              className,
            )}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
              }
              event.stopPropagation();
            }}
            tabIndex={0}
          >
            {compact}
          </span>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">
          {estimateExplanation(estimate)}
        </TooltipContent>
      </Tooltip>
    );
  }

  return (
    <details
      className={cn(
        'min-w-0 max-w-full text-xs text-muted-foreground',
        className,
      )}
    >
      <summary className="min-w-0 max-w-full cursor-pointer list-none marker:hidden">
        {compact}
      </summary>
      <p className="mt-1">{estimateExplanation(estimate)}</p>
    </details>
  );
}
